// src/components/TrajectoryMap.tsx
// 軌跡マップ（版非依存・2026-07-09）
// 引いた語を点として打ち、前の点と線で結ぶ。
// 座標の決め方は layoutStrategy に委譲する。
// 2026-07-26・CEO方針決定：ロジカル版専用の意味座標＋軸表示（旧logicalLayoutStrategy）は廃止し、
// 全カード種別をmakeCategoryLayoutStrategy（稜線ティア方式）に統一（詳細はtrajectoryLayouts.ts冒頭参照）。
// これによりhasSemanticAxes・SUIT_COLORSは実質常にfalse/未使用になるが、layoutStrategyという
// 差し替え可能なインターフェース自体は残し、コードは削除せず温存する（後方互換・将来の再利用余地）。

"use client";

import { useEffect, useState } from "react";
import type { RecordEntry } from "@/types/Entry";
import {
  makeCategoryLayoutStrategy,
  CATEGORY_LAYOUT_Y_HALF_RANGE,
  type LayoutStrategy,
  type CurvePoint,
} from "@/lib/trajectoryLayouts";

interface TrajectoryMapProps {
  entries: RecordEntry[];
  /** trueのとき、保存済みセッション表示用のトレースアニメを再生する */
  animate?: boolean;
  /** 座標の決め方。未指定時はstandard相当の稜線ティアを使う（2026-07-26・全種別統一後の後方互換デフォルト） */
  layoutStrategy?: LayoutStrategy;
  /** layoutStrategyが色（suit）を返さない場合に使うフォールバック色 */
  color?: string;
}

// PLOT_JP座標→SVG座標変換。幅と高さを別々に指定できるようにし、横長キャンバス（非logical版）
// でも同じ関数を使えるようにする。x軸（引いた順）は常に±10固定。y軸は呼び出し側が渡す
// yHalfRangeで可変（2026-07-26追記：稜線ティアの実際の可動域±5に合わせて縮小するため。
// 詳細はtrajectoryLayouts.tsのCATEGORY_LAYOUT_Y_HALF_RANGE参照）。省略時は従来通り±10。
const toSVG = (plotX: number, plotY: number, sizeW: number, sizeH: number, yHalfRange = 10) => ({
  x: ((plotX + 10) / 20) * sizeW,
  y: ((yHalfRange - plotY) / (yHalfRange * 2)) * sizeH, // Y軸はSVGで反転
});

const SUIT_COLORS: Record<string, string> = {
  "♠": "#1e40af", // blue
  "♣": "#166534", // green
  "♦": "#b45309", // amber
  "♥": "#be123c", // rose
  "joker": "#7c3aed", // violet
};

// 2026-07-26：Kazuto実機確認の結果、①進行中キャラクターが「◉」（輪＋点）になっており、
// 既存の「点＝言葉のメルクマール」と同じ見た目で混同する②その場でバウンドして瞬間移動するだけで
// 線の上を移動していない、という2点の指摘を受けて再設計。
// - キャラクターは塗りつぶしの単色丸「⚫︎」のみにする（点のメルクマール＝輪＋点とは見た目を分ける）
// - 前の点→次の点へ、実際に座標を直線移動（グライド）させる
// - 移動時間は「区間の距離 ÷ 一定の速さ」で決める＝今は均等なペース。
//   将来「書くのにかかった時間」を反映する際は、この距離ベースの時間を実測時間に差し替える想定。
const WALK_SPEED_PX_PER_SEC = 70; // ⚫︎の移動速度（距離に比例した均等ペース）
const MIN_STEP_MS = 500;  // 区間が短くても最低これだけの時間はかける
const MAX_STEP_MS = 2500; // 区間が長くても長引きすぎないための上限
const FIRST_POINT_DELAY_MS = 400; // 最初の1点はグライド元が無いのですぐ置く

function dist(a: { x: number; y: number }, b: { x: number; y: number }) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function stepDurationFor(a: { x: number; y: number }, b: { x: number; y: number }) {
  const ms = (dist(a, b) / WALK_SPEED_PX_PER_SEC) * 1000;
  return Math.min(MAX_STEP_MS, Math.max(MIN_STEP_MS, ms));
}

export default function TrajectoryMap({
  entries,
  animate = false,
  layoutStrategy = makeCategoryLayoutStrategy("standard"),
  color,
}: TrajectoryMapProps) {
  // layoutStrategyが決めた座標（-10〜+10）をSVG座標に変換
  const { points: layoutPoints, hasSemanticAxes, curvePoints: layoutCurvePoints } = layoutStrategy(entries);

  // 2026-07-13：横長キャンバス化（軽量案）。logical（意味座標・軸ラベルあり）は正方形のまま維持し、
  // それ以外（稜線・スパイラル）だけ横に広げる。縦横比が変わるだけで座標計算自体（-10〜+10）は
  // 変更不要。回答数に応じた可変幅・横スクロールへの本格対応は今回未着手・後日検討。
  //
  // 2026-07-26・CEO承認：稜線ティアの正規化修正後も「平坦に見える」というKazuto指摘が続いたため、
  // データ側（実測レンジ正規化）ではなく表示側のレバーとして高さを200→400に倍増。急峻さは
  // 縦横比の相対値の問題という判断（同じ縦方向データの振れ幅でも、横に対して縦を伸ばせば
  // 見た目の起伏は強調される）。座標計算・正規化ロジックには一切触れていない。
  //
  // →7/26追記・Kazuto実機確認で発覚：SIZE_H拡大だけでは「余白が多すぎて意味が薄い」と判明。
  // 従来はy軸の変換レンジを理論値±10固定にしていたため、稜線ティアの実際の可動域（±5程度）
  // しか使わないのに枠は±10ぶん確保していて、上下に大きな空白ができていた。
  // hasSemanticAxes（現状は常にfalse・後方互換用に残置）のときだけ従来通り±10を使い、
  // 稜線ティア表示時はCATEGORY_LAYOUT_Y_HALF_RANGE（実際の可動域＋余白）を使うことで、
  // 枠の高さをそのまま起伏の強調に使い切るようにした。
  //
  // →7/26再追記・Kazuto実機確認で発覚：余白は解消されたが「枠自体が縦長なのは嫌だ」と指摘。
  // Y_HALF_RANGE縮小により振れ幅（横幅比）はSIZE_Hに比例して伸びる関係にあるため
  // （SIZE_H=200でも横幅比11.1%、400では22.2%）、SIZE_Hを元の200へ戻しても
  // Y_HALF_RANGE縮小の効果だけで従来比1.67倍（20px→33.3px）の改善が残る。
  // 見た目の縦横比（本来の3:2）を優先し、SIZE_H拡大というレバー自体は撤回する。
  const SIZE_H = 200;
  const SIZE_W = hasSemanticAxes ? 200 : 300;
  const Y_HALF_RANGE = hasSemanticAxes ? 10 : CATEGORY_LAYOUT_Y_HALF_RANGE;
  const CENTER_X = SIZE_W / 2;
  const CENTER_Y = SIZE_H / 2;

  const points = layoutPoints.map((p) => {
    const svg = toSVG(p.x, p.y, SIZE_W, SIZE_H, Y_HALF_RANGE);
    return { ...svg, suit: p.suit, text: p.text };
  });

  // 太さ・ゆらぎを持つ滑らかな接続線（categoryLayout等が返す場合のみ）
  const curvePoints: (CurvePoint & { svgX: number; svgY: number })[] = (layoutCurvePoints ?? []).map((cp) => {
    const svg = toSVG(cp.x, cp.y, SIZE_W, SIZE_H, Y_HALF_RANGE);
    return { ...cp, svgX: svg.x, svgY: svg.y };
  });
  // SVG座標系での太さの見え幅。radiusはbranchGenerator側の相対値（0.5〜7程度）のためスケールを掛ける
  const RADIUS_TO_STROKE = 0.9;

  // アニメ用：何点目まで表示するか（animate=falseなら全点即表示）
  const [revealCount, setRevealCount] = useState(animate ? 0 : points.length);

  // entriesが変わったらリセット
  useEffect(() => {
    setRevealCount(animate ? 0 : points.length);
  }, [animate, entries.length]);

  // 1点ずつ順番に追加。区間の距離に応じた時間（stepDurationFor）だけ待って次の点を出す。
  // 最初の点だけは「前の点」が無いのでFIRST_POINT_DELAY_MSで即置く。
  useEffect(() => {
    if (!animate) return;
    if (revealCount >= points.length) return;
    const delay =
      revealCount === 0
        ? FIRST_POINT_DELAY_MS
        : stepDurationFor(points[revealCount - 1], points[revealCount]);
    const t = setTimeout(() => setRevealCount((c) => c + 1), delay);
    return () => clearTimeout(t);
    // points自体は毎レンダー新しい参照になるためdepsには入れない（従来通りpoints.lengthのみ）。
  }, [animate, revealCount, points.length]);

  // ⚫︎（進行中キャラクター）の座標。前の点→今の点へ、rAFで直線グライドさせる。
  // CSSのtransformはSVGのviewBoxスケールとpx単位がズレる可能性があるため使わず、
  // cx/cyを直接、点の描画と同じユーザー座標系で毎フレーム更新する。
  const [walkerPos, setWalkerPos] = useState<{ x: number; y: number } | null>(null);
  // 2026-07-26：グライド中かどうかを直接持つ状態。
  // 以前は表示条件を revealCount < points.length で判定していたため、
  // 「最後の点へ向かうグライド」（revealCountがpoints.lengthに達した瞬間）だけ
  // 描画されずスキップされていた（Kazuto実機指摘：最後から2番目の点で⚫︎が消える）。
  // グライドの開始・終了を明示的なstateで管理し、最後の区間も含めて必ず表示されるようにする。
  const [isGliding, setIsGliding] = useState(false);

  useEffect(() => {
    if (!animate) {
      setWalkerPos(null);
      setIsGliding(false);
      return;
    }
    if (revealCount === 1) {
      // 最初の1点：グライド元が無いのでそのまま置く（移動中ではない）
      setWalkerPos(points[0]);
      setIsGliding(false);
      return;
    }
    if (revealCount < 2 || revealCount > points.length) {
      setIsGliding(false);
      return;
    }

    const from = points[revealCount - 2];
    const to = points[revealCount - 1];
    const duration = stepDurationFor(from, to);
    let rafId: number;
    const start = performance.now();
    setIsGliding(true);

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      setWalkerPos({
        x: from.x + (to.x - from.x) * t,
        y: from.y + (to.y - from.y) * t,
      });
      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        setIsGliding(false); // 到達したら消える（最後の点も含む）
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
    // 同上：pointsはdepsに入れず、revealCount変化時のみ発火させる
  }, [animate, revealCount]);

  if (points.length === 0) return null;

  // 表示対象（アニメ中は revealCount 点まで）
  const visible = points.slice(0, revealCount);

  // 2026-07-23：セッション内プロセス表現（新方針②）。
  // 座標・レイアウトロジックには一切触れず、既存の「最新点」の上に軽量なキャラクター（●）を
  // 重ねて表示するだけの演出。アニメ進行中（revealCountがpoints.lengthに達するまで）のみ表示し、
  // 到達後は消える＝「今まさに進んでいる」感を出す。MindGarden同様のパラメータ調整沼を避けるため、
  // 曲線生成や座標計算には手を加えない。
  const isTraveling = animate && walkerPos !== null && isGliding;
  // 色は「今向かっている（＝直近に置かれた）点」の版色を使う
  const walkerTargetPt = revealCount > 0 ? points[Math.min(revealCount, points.length) - 1] : null;
  const travelColor = walkerTargetPt
    ? ((walkerTargetPt.suit ? SUIT_COLORS[walkerTargetPt.suit] : undefined) ?? color ?? "#6b7280")
    : undefined;
  // curvePointsは点数が多いため、revealCountに対応する進行度（0〜1）で絞り込む
  const progress = points.length > 0 ? revealCount / points.length : 1;
  const visibleCurve = curvePoints.filter((cp) => cp.t <= progress);

  // ラベルがSVG端で切れないようviewBoxにパディングを追加
  const PAD = 18; // ラベル用の余白（px）
  const VB_W = SIZE_W + PAD * 2;
  const VB_H = SIZE_H + PAD * 2;

  return (
    <div className="my-3">
      <div className="text-xs text-gray-400 mb-1 text-center tracking-wide">
        あなたの軌跡
      </div>
      <svg
        width={SIZE_W}
        height={SIZE_H}
        viewBox={`${-PAD} ${-PAD} ${VB_W} ${VB_H}`}
        className="mx-auto block rounded-md bg-gray-50 border border-gray-100"
        style={{ display: "block" }}
      >
        {/* 軸線・軸ラベル：意味座標を持つレイアウト（logical）のときだけ表示 */}
        {hasSemanticAxes && (
          <>
            <line x1={CENTER_X} y1={0} x2={CENTER_X} y2={SIZE_H} stroke="#e5e7eb" strokeWidth={1} />
            <line x1={0} y1={CENTER_Y} x2={SIZE_W} y2={CENTER_Y} stroke="#e5e7eb" strokeWidth={1} />

            {/* 縦軸ラベル：上=広げる、下=深める（SVG余白内） */}
            <text x={CENTER_X} y={-PAD + 11} textAnchor="middle" fontSize={9} fill="#9ca3af">広げる</text>
            <text x={CENTER_X} y={SIZE_H + PAD - 3} textAnchor="middle" fontSize={9} fill="#9ca3af">深める</text>
            {/* 横軸ラベル：横軸ライン端の直下 */}
            <text x={4} y={CENTER_Y + 12} textAnchor="start" fontSize={9} fill="#9ca3af">静的</text>
            <text x={SIZE_W - 4} y={CENTER_Y + 12} textAnchor="end" fontSize={9} fill="#9ca3af">動的</text>
          </>
        )}

        {/* 軌跡の線（順番どおり・visible範囲のみ）
            curvePointsがあれば、太さ・ゆらぎを持つ滑らかな曲線として区間ごとに描画する
            （2026-07-20・カテゴリレイアウト用。無ければ従来通り直線で結ぶ・後方互換） */}
        {visibleCurve.length > 1
          ? visibleCurve.map((pt, i) => {
              if (i === 0) return null;
              const prev = visibleCurve[i - 1];
              const width = Math.max(0.6, ((prev.radius + pt.radius) / 2) * RADIUS_TO_STROKE);
              return (
                <line
                  key={`curve-${i}`}
                  x1={prev.svgX}
                  y1={prev.svgY}
                  x2={pt.svgX}
                  y2={pt.svgY}
                  stroke="#94a3b8"
                  strokeWidth={width}
                  strokeLinecap="round"
                />
              );
            })
          : visible.length > 1 &&
            visible.map((pt, i) => {
              if (i === 0) return null;
              const prev = visible[i - 1];
              return (
                <line
                  key={`line-${i}`}
                  x1={prev.x}
                  y1={prev.y}
                  x2={pt.x}
                  y2={pt.y}
                  stroke="#94a3b8"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
              );
            })}

        {/* 各点（visible範囲のみ） */}
        {visible.map((pt, i) => {
          const dotColor = (pt.suit ? SUIT_COLORS[pt.suit] : undefined) ?? color ?? "#6b7280";
          const isLatest = i === visible.length - 1;
          const labelY = pt.y < 12 ? pt.y + 16 : pt.y - 8;
          // 2026-07-26・Kazuto実機指摘で発覚：先頭・末尾の点は必ずx=0／x=SIZE_Wの端に来る
          // （x＝引いた順で-10〜+10へ均等配置されるため）。textAnchor="middle"のままだと
          // ラベルが枠の外側へはみ出して見切れることがあった。先頭は右（内側）へ、末尾は左
          // （内側）へラベルが伸びるようにanchorを切り替え、単一点（先頭=末尾）のときだけ
          // 従来通りmiddleのままにする。
          const isFirst = i === 0;
          const labelAnchor: "start" | "middle" | "end" =
            isFirst && isLatest ? "middle" : isFirst ? "start" : isLatest ? "end" : "middle";
          return (
            <g key={`pt-${i}`}>
              {isLatest && (
                <circle
                  cx={pt.x}
                  cy={pt.y}
                  r={8}
                  fill="none"
                  stroke={dotColor}
                  strokeWidth={1}
                  opacity={0.3}
                />
              )}
              <circle
                cx={pt.x}
                cy={pt.y}
                r={isLatest ? 5 : 4}
                fill={dotColor}
                opacity={isLatest ? 1 : 0.65}
              />
              {/* 2026-07-13：ラベルの読みにくさ対応。背景帯を敷く方式（rgba(31,41,55,0.72)）を
                  試したが、色によって文字が背景に沈み逆に読みにくくなったため撤回（Kazuto実機確認
                  「めちゃくちゃ読みにくい」）。文字色を版に関係なく濃いグレー固定にし、ドットだけ
                  版の色を保つ方式に変更（Kazuto提案の代替案）。 */}
              <text
                x={pt.x}
                y={labelY}
                textAnchor={labelAnchor}
                fontSize={8}
                fill="#1f2937"
                fontWeight={isLatest ? "bold" : "normal"}
                opacity={isLatest ? 1 : 0.85}
              >
                {pt.text}
              </text>
            </g>
          );
        })}

        {/* 進行中キャラクター（⚫︎＝塗りつぶし単色丸のみ。点のメルクマール◉＝輪＋点とは見た目を分ける。
            前の点→今の点へ実際に座標を直線移動する。座標計算（点・線のレイアウト）自体には無関係） */}
        {isTraveling && walkerPos && (
          <circle cx={walkerPos.x} cy={walkerPos.y} r={5} fill={travelColor} opacity={0.9} />
        )}
      </svg>

      {/* 保存への誘導（2点以上で表示・アニメ中は非表示） */}
      {!animate && points.length >= 2 && (
        <p className="text-center text-xs text-gray-400 mt-1">
          保存すると、この経路が残ります
        </p>
      )}
    </div>
  );
}
